import Badge from 'components/base/Badge';
import Button from 'components/base/Button';
import ProcesoDetailsSummary from 'components/modules/project-management/project-details/ProcesoDetailsSummary';
import MovimientosTimeline from 'components/timelines/MovimientosTimeline';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';
import { useState, useEffect } from 'react';
import { Col, Row, Modal, Form, Spinner, Card } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Proceso, procesoInicial } from 'data/project-management/procesos';
import Select, { SingleValue } from 'react-select';
import { Movimiento } from 'data/project-management/Movimiento';
import ChatWidget from 'components/common/chat-widget/ChatWidget';
import { useAppContext } from 'providers/AppProvider'; 
import ChatWidgetProvider, { useChatWidgetContext } from 'providers/ChatWidgetProvider'; 
import { User, useAuth } from 'providers/AuthProvider';
import { useMemo } from 'react';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import CajaListTable, { getCajaListTableColumns } from 'components/tables/CajaListTable';
import { MovimientoDeCaja } from 'data/project-management/movimientoDeCaja';
import CalendarProvider, { useCalendarContext } from 'providers/CalendarProvider';
import { SET_CALENDAR_STATE } from 'reducers/CalendarReducer';
import CalendarAddNewEventModal from 'components/modals/CalendarAddNewEventModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';

// Importa los componentes para renderizarlos en el modal (Ajusta la ruta si es necesario)
import MovimientoComponent from './MovimientoComponent'; 
import CobroDetail from './CobroDetail';

interface TipoDeEstado {
  id: number;
  estado: string;
}

interface Estado {
  procEstId: number;
  estado: string;
}

interface GeminiTaskOption {
  value: string;
  label: string;
}

interface WhatsAppRow {
  id: number | string;
  direction: 'inbound' | 'outbound';
  createdat?: string;
  created_at?: string;
  createdAt?: string;
  from: string;
  to: string;
  body: string;
  read_at?: string | null;
  status?: string;
  miem?: string;
}

interface MovimientoFinancieroItem {
  movimientos_general?: MovimientoDeCaja[];
  [key: string]: unknown;
}

interface DatosFinancieros {
  cobros?: MovimientoFinancieroItem[];
  pagos?: MovimientoFinancieroItem[];
}

const ProcesoDetailsContent = () => {
  const { config, setConfig } = useAppContext();
  const { setConversation } = useChatWidgetContext();
  const [proc, setProc] = useState<Proceso>(procesoInicial);
  const [mtos, setMtos] = useState<Movimiento[]>([]);
  const [mtosSugeridos, setMtosSugeridos] = useState<Movimiento[]>([]); 
  const [tipoDeEstado, setTipoDeEstado] = useState<{value: number, label: string}[]>([]); 
  const [estados, setEstados] = useState<Estado[]>([]);
  const { id } = useParams();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';
  const { setContentClass } = useMainLayoutContext();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Gemini
  const [showEstadosModal, setShowEstadosModal] = useState(false);
  const [showGeminiModal, setShowGeminiModal] = useState(false);
  const [loadingGemini, setLoadingGemini] = useState(false);
  const [geminiResponse, setGeminiResponse] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedTask, setSelectedTask] = useState<GeminiTaskOption>({ 
    value: 'workflow', label: 'Pasos a seguir en este proceso' 
  });

  const taskOptions: GeminiTaskOption[] = [
    { value: 'workflow', label: 'Pasos a seguir en este proceso' },
    { value: 'comms', label: 'Contestar WhatsApp / Comunicación' },
    { value: 'analyze_alegato', label: 'Analizar Proyecto de Alegato' },
    { value: 'analyze', label: 'Elaborar prompt personalizado' }
  ];

  // Finanzas
  const [showCajaModal, setShowCajaModal] = useState(false);
  const [datosFinancieros, setDatosFinancieros] = useState<DatosFinancieros | null>(null);  
  const [loadingFinanzas, setLoadingFinanzas] = useState(false);

  // Modals de Movimientos / Cobros
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState<'cobro' | 'movimiento' | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<number | string | null>(null);

  const { calendarDispatch } = useCalendarContext(); // <-- NUEVO

  const fetchMtos = async () => {
    try {
      const response = await axios.get(`${apiUrl}/mtos/proc/${id}`);
      setMtos(response.data);
    } catch (error) {
      console.error('ha habido un error: ', error);
    }
  };

  const fetchMtosSugeridos = async () => {
    try {
      const response = await axios.get(`${apiUrl}/mtos_sugeridos/proc/${id}`);
      setMtosSugeridos(response.data);
    } catch (error) {
      console.error('Error al obtener movimientos sugeridos: ', error);
    }
  };

  useEffect(() => {
    if (showCajaModal && proc?.PROC && !datosFinancieros) {
      fetchFinanzas();
    }
  }, [showCajaModal, proc?.PROC]);

  const fetchFinanzas = async () => {
    setLoadingFinanzas(true);
    try {
      const response = await fetch(`${apiUrl}/procesos/detalles-financieros/${proc.PROC}`);
      const data = await response.json();
      setDatosFinancieros(data);
    } catch (error) {
      console.error('Error trayendo finanzas:', error);
    } finally {
      setLoadingFinanzas(false);
    }
  };

  const movimientosAplanados = useMemo(() => {
    if (!datosFinancieros) return [];
    let movimientos: MovimientoDeCaja[] = [];
    if (datosFinancieros.cobros && Array.isArray(datosFinancieros.cobros)) {
      datosFinancieros.cobros.forEach((cobro: MovimientoFinancieroItem) => {
        if (cobro.movimientos_general && Array.isArray(cobro.movimientos_general)) {
          movimientos = [...movimientos, ...cobro.movimientos_general];
        }
      });
    }
    if (datosFinancieros.pagos && Array.isArray(datosFinancieros.pagos)) {
      datosFinancieros.pagos.forEach((pago: MovimientoFinancieroItem) => {
        if (pago.movimientos_general && Array.isArray(pago.movimientos_general)) {
          movimientos = [...movimientos, ...pago.movimientos_general];
        }
      });
    }
    movimientos.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    return movimientos;
  }, [datosFinancieros]);

  const dynamicColumns = useMemo(() => getCajaListTableColumns(true, true), []);
  const table = useAdvanceTable<MovimientoDeCaja>({
    data: movimientosAplanados,
    columns: dynamicColumns,
    pageSize: 10,
    pagination: true,
    sortable: true
  });

  useEffect(() => {
    const fetchProc = async () => {
      try {
        const response = await axios.get(`${apiUrl}/procesos/id/${id}`);
        setProc(response.data);
      } catch (error) {
        console.error('ha habido un error: ', error);
      }
    };
    fetchProc();
  }, [id, apiUrl]);

  useEffect(() => {
    fetchMtos();
    fetchMtosSugeridos();
  }, [id, apiUrl]);

  useEffect(() => {
    const fetchEstados = async () => {
      try {
        const response = await axios.get(`${apiUrl}/procEstM2m/proc/${id}`);
        setEstados(response.data);
      } catch (error) {
        console.error('Error fetching estados:', error);
        setEstados([]);
      }
    };
    fetchEstados();
  }, [id, apiUrl]);

  useEffect(() => {
    const fetchTEstados = async () => {
      try {
        const response = await axios.get(`${apiUrl}/tEstado`);
        const formattedOptions = response.data.map((item: TipoDeEstado) => ({        
          value: item.id,        
          label: item.estado      
        }));      
        setTipoDeEstado(formattedOptions);  
      } catch (error) {
        console.error('ha habido un error: ', error);
      }
    };
    fetchTEstados();
  }, [apiUrl]);

  useEffect(() => {
    setContentClass('px-0 pt-navbar');
    return () => setContentClass('');
  }, [setContentClass]);

  useEffect(() => {
    setConfig({ isChatWidgetVisible: true });
    return () => setConfig({ isChatWidgetVisible: false });
  }, []);

  useEffect(() => {
    const fetchWhatsapps = async () => {
      if (!proc || !proc.ACTOR) return;
      try {
        const response = await axios.get<WhatsAppRow[]>(`${apiUrl}/whatsapps/persona/${proc.ACTOR}`);
        const datosDB = response.data;
        let numeroCelular = '';
        const mensajesInbound = datosDB.filter((fila) => fila.direction === 'inbound');
        
        if (mensajesInbound.length > 0) {
          mensajesInbound.sort((a, b) => {
            const fechaA = a.createdat || a.created_at || a.createdAt || '';
            const fechaB = b.createdat || b.created_at || b.createdAt || '';
            return new Date(fechaB).getTime() - new Date(fechaA).getTime();
          });
          numeroCelular = mensajesInbound[0].from;
        } else if (datosDB.length > 0) {
          numeroCelular = datosDB[0].to;
        }

        if (!numeroCelular) {
          try {
            const celResponse = await axios.get(`${apiUrl}/personasCelulares/principal/${proc.ACTOR}`);
            if (celResponse.data && celResponse.data.celular) {
              numeroCelular = celResponse.data.celular;
            }
          } catch (error) {
            console.log("No se encontró celular principal en la base de datos para esta persona.");
          }
        }

        const obtenerFechaValida = (fila: WhatsAppRow) => {
          let fechaCruda = fila.createdAt || fila.created_at || fila.createdat;
          if (!fechaCruda) return new Date(0);
          if (typeof fechaCruda === 'string') {
            fechaCruda = fechaCruda.replace(' ', 'T'); 
          }
          return new Date(fechaCruda);
        };

        const datosOrdenados = [...datosDB].sort((a, b) => {
          return obtenerFechaValida(a).getTime() - obtenerFechaValida(b).getTime();
        });

        const mensajesMapeados = datosOrdenados.map((fila) => {
          const fechaLimpia = obtenerFechaValida(fila);
          const horaMostrada = fechaLimpia.getTime() !== 0 
            ? fechaLimpia.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : 'Sin hora';

          return {
            id: fila.id, 
            message: fila.body, 
            miem: fila.miem || 'Sistema',
            type: fila.direction === 'inbound' ? ('received' as const) : ('sent' as const), 
            time: horaMostrada, 
            readAt: fila.read_at ? fila.read_at : (fila.status === 'read' ? 'Leído' : null)
          };
        });

        const conversacionLista = {
          id: proc.ACTOR || 'sin-id',
          user: { 
            id: proc.ACTOR || 'sin-id', 
            name: proc.ACTO || 'Usuario',
            avatar: 'assets/img/team/40x40/avatar.webp',
            status: 'online' as const, 
            celular: numeroCelular 
          },
          messages: mensajesMapeados
        };

        setConversation(conversacionLista);

      } catch (error) {
        console.error("Error al cargar los datos para el widget:", error);
      }
    };
    fetchWhatsapps();
  }, [proc.ACTOR, proc.ACTO, setConversation, apiUrl]);

  const handleButtonClickEstados = () => setShowEstadosModal(true);

  const handleTEstadoSelectChange = async (selectedOption: SingleValue<{ value: number; label: string }>) => {
    if (!selectedOption) return;
    const selectedEstadoId = selectedOption.value;
    try {
      await axios.post(`${apiUrl}/procEstM2m/create`, { proc: id, estado: selectedEstadoId });
      const response = await axios.get(`${apiUrl}/procEstM2m/proc/${id}`);
      setEstados(response.data);
    } catch (error) {
      console.error('Error updating selected estado:', error);
    }
  };

  const handleDeleteEstado = async (procEstId: number) => {
    try {
      await axios.delete(`${apiUrl}/procEstM2m/delete/${procEstId}`); 
      const response = await axios.get(`${apiUrl}/procEstM2m/proc/${id}`);
      setEstados(response.data);
    } catch (error) {
      console.error('Error deleting estado:', error);
    }
  };

  const handleSupervisadoButton = () => {
    axios.post(`${apiUrl}/procesosSupervision/update`, { proc: proc.PROC })
      .then(response => console.log('Update successful:', response.data))
      .catch(error => console.error('Error updating:', error))
      .finally(() => navigate('/app/apps/project-management/procesos-list-view'));
  };

  const handleButtonClickNewMto = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${apiUrl}/mtos/store`, {
        proc: proc.PROC
      });
      const newId = response.data.id;
      
      setSelectedItemType('movimiento');
      setSelectedItemId(newId);
      setShowItemModal(true);
    } catch (error) {
      console.error('Error creating row:', error);
    }
  };

  const handleTimelineItemClick = async (item: Movimiento) => {
    const claseLimpia = item?.clase_de_mto?.trim().toLowerCase();
    const isCobro = claseLimpia === 'cobro' || item?.descripcion?.substring(0, 5) === 'Cobro';
    const isEvento = claseLimpia === 'evento';
    
    if (isCobro && item.clase_fk) {
      setSelectedItemType('cobro');
      setSelectedItemId(item.clase_fk);
      setShowItemModal(true);
      
    } else if (isEvento) {
      if (item.clase_fk) {
        try {
          const response = await axios.get(`${apiUrl}/eventos/${item.clase_fk}`);
          const eventoDb = response.data;
          
  const mockEvent = {
            id: String(eventoDb.id),
            start: eventoDb.start ? new Date(eventoDb.start) : new Date(),
            end: eventoDb.end ? new Date(eventoDb.end) : null,
            extendedProps: { ...eventoDb }
          };
          
          calendarDispatch({
            type: SET_CALENDAR_STATE,
            payload: { 
              openNewEventModal: true, 
              selectedEvent: mockEvent as never 
            }
          });
          
        } catch (error) {
          console.error('Error fetching evento:', error);
          alert('No se pudo cargar el evento del calendario. Puede que haya sido eliminado.');
        }
      } else {
        alert('Este movimiento es un Evento, pero no tiene datos de calendario asociados en la base de datos (clase_fk está vacío).');
           
        setSelectedItemType('movimiento');
        setSelectedItemId(item.id);
        setShowItemModal(true);
      }
      
    } else {
      setSelectedItemType('movimiento');
      setSelectedItemId(item.id);
      setShowItemModal(true);
    }
  };

  const handleCloseItemModal = () => {
    setShowItemModal(false);
    setSelectedItemType(null);
    setSelectedItemId(null);
    fetchMtos();
  };

  const handleGeminiCall = async () => {
    setLoadingGemini(true);
    setGeminiResponse('');
    try {
      const estadosActuales = estados.map(e => e.estado);
      const payload = {
        inputData: { instruccion: customPrompt, movimientos: mtos, estados_actuales: estadosActuales },
        caseId: id
      };
      const response = await axios.post(`${apiUrl}/api/v1/ai/execute/${selectedTask.value}`, payload);
      const data = response.data.data;
      setGeminiResponse(typeof data === 'object' ? JSON.stringify(data, null, 2) : data);
    } catch (error) {
      console.error('Error con Gemini:', error);
      setGeminiResponse('Hubo un error al conectar con Gemini.');
    } finally {
      setLoadingGemini(false);
    }
  };

  return (
    <Row className="g-0 w-100 overflow-hidden">
      <Col xs={12} xxl={8} className="px-0 bg-body">
        <div className="px-4 px-lg-6 pt-5 pb-9">
          
          {/* Header Summary */}
          <Row className="align-items-center mb-4 pb-3 border-bottom g-3">
            <Col xs={12} lg={6}>
              <h2 className="text-body-emphasis fw-bolder mb-0 text-wrap">
                {proc?.AUX8}
              </h2>
            </Col>
            <Col xs={12} lg={6} className="d-flex justify-content-lg-end">
              <ProcesoDetailsSummary {...proc} />
            </Col>
          </Row>

          {/* Buttons & Statuses */}
          <Row className="gx-0 gx-sm-5 gy-4 mb-5">
            <Col xs={12} xl={12}>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {estados.length > 0 ? (
                  estados.map((est) => (
                    <Badge key={est.procEstId} variant="tag" className="bg-secondary">
                      {est.estado}
                    </Badge>
                  ))
                ) : (
                  <Badge variant="tag" className="bg-secondary">Proceso Sin Estados</Badge>
                )}
              </div>

              <div className="d-flex flex-wrap gap-2">
                <Button variant="primary" onClick={handleButtonClickNewMto}>Agregar Movimiento</Button>
                <Button variant="primary" onClick={handleButtonClickEstados}>Estados</Button>
                <Button variant="primary" onClick={() => setShowGeminiModal(true)}>JAP GPT</Button>
                <Button variant="primary" onClick={() => setShowCajaModal(true)}>Movimientos De Caja</Button>
                {user?.nivel_acceso === 'socio' && (
                <Button variant="primary" onClick={handleSupervisadoButton}>Supervisado</Button>
                )}
              </div>
              
              <Modal show={showEstadosModal} onHide={() => setShowEstadosModal(false)} size="lg">
                <Modal.Header closeButton><Modal.Title>Estados</Modal.Title></Modal.Header>
                <Modal.Body>
                  {/* ... (Contenido original) ... */}
                  <h4>Estados Actuales</h4>
                  <div className="d-flex flex-wrap gap-2 mb-4">
                    {estados.map((est) => (
                      <Badge key={est.procEstId} variant="tag" className="bg-secondary d-flex align-items-center gap-1">
                        {est.estado}
                        <Button variant="link" className="p-0 text-white lh-1 fs-8 ms-1" onClick={() => handleDeleteEstado(est.procEstId)}>&times;</Button>
                      </Badge>
                    ))}
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label>Seleccionar Nuevo Estado</Form.Label>
                    <Select id="selectTipoDeEstado" options={tipoDeEstado} onChange={handleTEstadoSelectChange} placeholder="Seleccione Nuevo Estado" isClearable />
                  </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowEstadosModal(false)}>Cerrar</Button>
                </Modal.Footer>
              </Modal>

            </Col>
          </Row>

          <Row className="mt-4">
            <Col xs={12} lg={12}>
              <MovimientosTimeline data={mtos} onItemClick={handleTimelineItemClick} />
            </Col>
          </Row>

          {mtosSugeridos.length > 0 && (
            <Row className="mt-5">
              <Col xs={12} lg={12}>
                <Card className="border-warning border-2 bg-warning-subtle shadow-sm">
                  <Card.Body>
                    <h4 className="text-warning-emphasis mb-3">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                      Movimientos Sugeridos / Próximos Pasos
                    </h4>
                    <MovimientosTimeline data={mtosSugeridos} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </div>
      </Col>

      <Modal show={showGeminiModal} onHide={() => setShowGeminiModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>JAP GPT</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Seleccionar Tarea</Form.Label>
            <Select 
              options={taskOptions} 
              defaultValue={selectedTask}
              onChange={(val: SingleValue<GeminiTaskOption>) => {
                if (val) setSelectedTask(val);
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Instrucciones o Prompt (Opcional)</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3} 
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Escribe aquí lo que necesitas específicamente..."
            />
          </Form.Group>
          {loadingGemini && (
            <div className="text-center my-3">
              <Spinner animation="border" variant="primary" />
              <p>JAP GPT está procesando el caso...</p>
            </div>
          )}
          {geminiResponse && (
            <div className="mt-3 p-3 bg-light border rounded">
              <h6>Respuesta:</h6>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {geminiResponse}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGeminiModal(false)}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleGeminiCall} disabled={loadingGemini}>
            Consultar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCajaModal} onHide={() => setShowCajaModal(false)} size="xl"> {/* Cambiado a xl para que la tabla entre cómoda */}
        <Modal.Header closeButton>
          <Modal.Title>Movimientos De Caja - Proceso: {proc?.AUX7}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFinanzas ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Cargando datos financieros...</p>
            </div>
          ) : movimientosAplanados.length > 0 ? (
            <AdvanceTableProvider {...table}>
              <CajaListTable />
            </AdvanceTableProvider>
          ) : (
            <div className="text-center py-5 text-muted">
              No hay movimientos de caja registrados para este proceso.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCajaModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showItemModal} onHide={handleCloseItemModal} size="xl">
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body className="p-0 bg-body">
          {selectedItemType === 'cobro' && selectedItemId && (
            <CobroDetail 
              cobroId={selectedItemId} 
              isModal={true} 
              onClose={handleCloseItemModal} 
            />
          )}
          {selectedItemType === 'movimiento' && selectedItemId && (
            <MovimientoComponent 
              movimientoId={selectedItemId} 
              isModal={true} 
              onClose={handleCloseItemModal} 
            />
          )}
        </Modal.Body>
      </Modal>
      
      <CalendarAddNewEventModal />
      <ChatWidget />
    </Row>
  );
};

const ProcesoDetails = () => (
  <CalendarProvider>
    <ChatWidgetProvider>
      <ProcesoDetailsContent />
    </ChatWidgetProvider>
  </CalendarProvider>
);

export default ProcesoDetails;